/* OS EDD - Try2 (scoped, no globals) */
(() => {
  const SELECTOR_ROOT = '[data-os-edd-try2]';
  const SELECTOR_CTA = '[data-osedd2-cta]';
  const CHECKOUT_REDIRECT_FLAG = '__oseddCheckoutRedirectUntil';

  function parseDiscountCodes(raw) {
    const text = (raw || '').trim();
    if (!text) return [];
    return text
      .split(/[\n,]+/g)
      .map((s) => s.trim())
      .filter(Boolean);
  }

  function getSectionConfig(root) {
    return {
      bogoUrlParamsEnabled: root && root.dataset.bogoUrlParamsEnabled === 'true',
      bogoUrlParams: (root && root.dataset.bogoUrlParams) || '',
      checkoutUrl: (root && root.dataset.checkoutUrl) || '/checkout',
      giftCardAttrSubEnabled: root && root.dataset.giftCardAttrSubEnabled === 'true',
      giftCardAttrOtpEnabled: root && root.dataset.giftCardAttrOtpEnabled === 'true',
      giftCardAmount: (root && root.dataset.giftCardAttrAmount) || '',
    };
  }

  function getPromoGiftItems(root) {
    if (!root || root.dataset.customPromoEnabled !== 'true') return [];
    const landingSource = (root.dataset.customPromoLandingSource || '').trim();
    const rawIds = (root.dataset.customPromoGiftIds || '').trim();
    if (!landingSource || !rawIds) return [];
    return rawIds.split(',').map((s) => s.trim()).filter(Boolean).map((id) => ({
      id: Number(id),
      quantity: 1,
      properties: {
        _landing_source: landingSource,
        __landing_source: landingSource,
        _promo_gift: 'true',
      },
    }));
  }

  function attachPromoLandingSource(item, root) {
    if (!root || root.dataset.customPromoEnabled !== 'true') return item;
    const landingSource = (root.dataset.customPromoLandingSource || '').trim();
    if (!landingSource) return item;
    return Object.assign({}, item, {
      properties: Object.assign({}, item.properties || {}, {
        _landing_source: landingSource,
        __landing_source: landingSource,
      }),
    });
  }

  function normalizeBogoParamString(rawValue) {
    if (!rawValue) return '';
    let normalized = String(rawValue).trim();
    if (!normalized) return '';

    if (normalized.indexOf('://') !== -1) {
      try {
        normalized = new URL(normalized).search.replace(/^\?/, '');
      } catch (err) {}
    }

    const queryStartIndex = normalized.indexOf('?');
    if (queryStartIndex > -1) {
      normalized = normalized.slice(queryStartIndex + 1);
    }

    normalized = normalized.replace(/^[?&]+/, '');

    const hashIndex = normalized.indexOf('#');
    if (hashIndex > -1) {
      normalized = normalized.slice(0, hashIndex);
    }

    return normalized.trim();
  }

  function applyBogoUrlParams(root, options) {
    const sectionConfig = getSectionConfig(root);
    const bogo = window.AmbrosiaBogoUrlParams;
    if (!bogo) return false;
    return bogo.apply(sectionConfig, options);
  }

  function sanitizeErrorMessage(raw) {
    const text = String(raw || '').trim();
    if (!text) return '';

    const looksLikeHtml =
      /<!doctype html/i.test(text) ||
      /<html[\s>]/i.test(text) ||
      /_cf_chl/i.test(text) ||
      /<script[\s>]/i.test(text);

    if (looksLikeHtml) {
      return 'Unable to reach the store cart. Please refresh the page and try again.';
    }

    if (text.length > 280) {
      return text.slice(0, 277) + '...';
    }

    return text;
  }

  function extractFetchError(data, isJson) {
    if (isJson && data && typeof data === 'object') {
      return sanitizeErrorMessage(data.description || data.message || 'Request failed');
    }

    if (typeof data === 'string') {
      return sanitizeErrorMessage(data || 'Request failed');
    }

    return 'Request failed';
  }

  function extractBogoCartAttributes(root) {
    const sectionConfig = getSectionConfig(root);
    const raw = normalizeBogoParamString(sectionConfig.bogoUrlParams);
    if (!raw) return null;
    const attrs = {};
    let hasAny = false;
    new URLSearchParams(raw).forEach((value, key) => {
      if (!key) return;
      attrs[key] = value;
      hasAny = true;
    });
    return hasAny ? attrs : null;
  }

  function waitForBogoGift(knownItemCount) {
    return new Promise((resolve) => {
      let elapsed = 0;
      const interval = 200;
      const maxWait = 3000;

      function check() {
        fetch('/cart.js', { credentials: 'same-origin' })
          .then((r) => r.json())
          .then((cart) => {
            if (cart.item_count > knownItemCount) {
              resolve(true);
            } else {
              elapsed += interval;
              if (elapsed >= maxWait) {
                resolve(false);
              } else {
                setTimeout(check, interval);
              }
            }
          })
          .catch(() => resolve(false));
      }

      setTimeout(check, interval);
    });
  }

  function mergeConfiguredBogoParams(checkoutUrl, root) {
    const sectionConfig = getSectionConfig(root);
    if (!sectionConfig.bogoUrlParamsEnabled) return;

    const sanitizedParams = normalizeBogoParamString(sectionConfig.bogoUrlParams);
    if (!sanitizedParams) return;

    const incomingParams = new URLSearchParams(sanitizedParams);
    incomingParams.forEach((value, key) => {
      if (!key) return;
      checkoutUrl.searchParams.set(key, value);
    });
  }

  function getCheckoutUrlWithCurrentParams(root, discountCodes) {
    const sectionConfig = getSectionConfig(root);
    const checkoutBase = sectionConfig.checkoutUrl || '/checkout';
    const checkoutUrl = new URL(checkoutBase, window.location.origin);
    const currentParams = new URLSearchParams(window.location.search);

    currentParams.forEach((value, key) => {
      checkoutUrl.searchParams.set(key, value);
    });

    mergeConfiguredBogoParams(checkoutUrl, root);

    if (discountCodes && discountCodes.length > 0) {
      checkoutUrl.searchParams.set('discount', discountCodes.join(','));
    }

    if (checkoutUrl.origin === window.location.origin) {
      return checkoutUrl.pathname + checkoutUrl.search + checkoutUrl.hash;
    }
    return checkoutUrl.toString();
  }

  async function postJSON(url, bodyObj) {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(bodyObj),
      credentials: 'same-origin',
    });

    const contentType = res.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');
    const data = isJson ? await res.json() : await res.text();

    if (!res.ok) {
      throw new Error(extractFetchError(data, isJson));
    }

    return data;
  }

  async function addItemsBatch(items) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ items: items || [] }),
      credentials: 'same-origin',
      cache: 'no-store',
    });

    const contentType = res.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');
    const data = isJson ? await res.json() : await res.text();

    if (!res.ok) {
      throw new Error(extractFetchError(data, isJson));
    }

    return data;
  }

  async function addSingleItem(item) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(item || {}),
      credentials: 'same-origin',
      cache: 'no-store',
    });

    const contentType = res.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');
    const data = isJson ? await res.json() : await res.text();

    if (!res.ok) {
      throw new Error(extractFetchError(data, isJson));
    }

    return data;
  }

  function wait(ms) {
    return new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });
  }

  function setStatus(root, msg) {
    const el = root.querySelector('[data-osedd2-status]');
    if (!el) return;
    el.textContent = sanitizeErrorMessage(msg);
  }

  function setBusy(btn, isBusy) {
    if (!btn) return;
    btn.disabled = !!isBusy;
    btn.setAttribute('aria-busy', isBusy ? 'true' : 'false');

    if (isBusy) btn.classList.add('is-busy');
    else btn.classList.remove('is-busy');
  }

  function getScrollTargetElement(rawTarget) {
    const targetId = (rawTarget || '').trim().replace(/^#/, '');

    if (!targetId) return null;

    return document.getElementById(targetId);
  }

  function handleScrollClick(btn) {
    const root = btn.closest(SELECTOR_ROOT);
    if (!root) return;

    const rawTarget = btn.getAttribute('data-osedd-scroll-target') || '';
    const targetId = rawTarget.trim().replace(/^#/, '');
    const targetEl = getScrollTargetElement(rawTarget);

    if (!targetEl) {
      setStatus(
        root,
        targetId
          ? `Could not find section ID "${targetId}". Please check the CTA scroll target setting.`
          : 'Missing scroll target section ID. Please set it in the section settings.'
      );
      return;
    }

    setStatus(root, '');

    targetEl.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }

  function handleCtaClick(btn) {
    const action = (btn.getAttribute('data-osedd-cta-action') || 'checkout')
      .trim()
      .toLowerCase();

    if (action === 'scroll') {
      handleScrollClick(btn);
      return;
    }

    handleCheckoutClick(btn);
  }

  function setCheckoutRedirectGuard(durationMs = 6000) {
    window[CHECKOUT_REDIRECT_FLAG] = Date.now() + Math.max(1000, Number(durationMs) || 6000);
  }

  function clearCheckoutRedirectGuard() {
    delete window[CHECKOUT_REDIRECT_FLAG];
  }

  async function handleCheckoutClick(btn) {
    const root = btn.closest(SELECTOR_ROOT);
    if (!root) return;

    if (root.__osedd2CheckoutSubmitting) return;
    root.__osedd2CheckoutSubmitting = true;

    setStatus(root, '');
    setBusy(btn, true);

    try {
      if (!window.AmbrosiaFastCheckout) {
        throw new Error('Checkout helper failed to load. Please refresh and try again.');
      }

      const clearCart = btn.getAttribute('data-osedd-clear-cart') === '1';
      const mainVariantIdRaw = (btn.getAttribute('data-osedd-main-variant') || '').trim();
      const mainQty = Number(btn.getAttribute('data-osedd-main-qty') || 1);
      const mainSellingPlanRaw = (btn.getAttribute('data-osedd-main-selling-plan') || '').trim();

      const mainVariantId = Number(mainVariantIdRaw || 0);
      const mainSellingPlanId = Number(mainSellingPlanRaw || 0);

      if (!mainVariantId) {
        throw new Error('Missing main variant ID. Please set it in the section settings.');
      }

      let freebies = [];
      const freebiesRaw = btn.getAttribute('data-osedd-freebies') || '[]';

      try {
        freebies = JSON.parse(freebiesRaw) || [];
      } catch (e) {
        freebies = [];
      }

      let mainItem = attachPromoLandingSource({
        id: mainVariantId,
        quantity: Math.max(1, mainQty || 1),
      }, root);

      if (mainSellingPlanRaw && mainSellingPlanId > 0) {
        mainItem.selling_plan = mainSellingPlanId;
      }

      const freebieItems = (freebies || [])
        .filter((it) => it && Number(it.id) > 0)
        .map((it) => ({
          id: Number(it.id),
          quantity: Math.max(1, Number(it.quantity) || 1),
        }));

      const promoGiftItems = getPromoGiftItems(root);
      const sectionConfig = getSectionConfig(root);
      await window.AmbrosiaFastCheckout.addThenCheckout({
        sectionConfig: sectionConfig,
        clearCart: clearCart,
        items: [mainItem].concat(freebieItems).concat(promoGiftItems),
        fallbackItems: [mainItem],
        waitForGift: sectionConfig.bogoUrlParamsEnabled,
        discountCodes: parseDiscountCodes(btn.getAttribute('data-osedd-discounts')),
      });
      return;
    } catch (err) {
      setStatus(
        root,
        err && err.message ? err.message : 'Unable to start checkout. Please try again.'
      );
      setBusy(btn, false);
      root.__osedd2CheckoutSubmitting = false;
      if (window.AmbrosiaFastCheckout) window.AmbrosiaFastCheckout.endRedirecting();
    }
  }

  function bindRoot(root) {
    if (!root || root.__osedd2Bound) return;
    root.__osedd2Bound = true;

    if (applyBogoUrlParams(root, { allowReload: true })) return;

    const cta = root.querySelector(SELECTOR_CTA);
    if (cta) {
      cta.addEventListener('click', () => handleCtaClick(cta));
    }
  }

  function init() {
    document.querySelectorAll(SELECTOR_ROOT).forEach(bindRoot);
  }

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();